import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { MY_MARKS_KEY, MY_QUESTIONS_KEY, myEntriesKey, type Awaiting, type MyMark, type MyQuestion, type RepeatEvery } from './myCalendar';
import { refreshCrew } from './refreshCrew';
import type { MyAssignment } from './MyCalendarPage';
import { PRIVATE_BASE, PRIVATE_KEY, type PrivateTask } from '../private-tasks/types';

/** What „Mój kalendarz” saves for my days (`POST /api/me/availability`): never a person — the server takes mine. */
export type MarkBody = {
  days: string[];
  state: 'available' | 'unavailable' | null;
  hours?: { from: string; to: string };
  repeat?: { every: RepeatEvery; until: string };
  withdraw?: boolean;
  /** Review M6: the assignments the question named — the answer is about them only. */
  assignmentIds?: string[];
};
/** „Wycofujesz się z obsady: …?” — the server's question before a „Niedostępny” on days I work. */
export type WithdrawQuestion = { body: MarkBody; message: string; assignmentIds: string[] };

/**
 * My month: my marks, my events (`/api/me/calendar`), all my questions and (owner 2026-11-03 23:41) my private tasks due
 * in the weeks shown (`/api/me/private-tasks?from&to` — only mine, under the private tasks' key, so a save in their
 * dialog refreshes the month).
 */
export function useMyMonth(range: { from: string; to: string }) {
  const marks = useQuery({
    queryKey: [...MY_MARKS_KEY, range.from, range.to],
    placeholderData: keepPreviousData,
    queryFn: () => api<{ person: { id: string; name: string } | null; days: MyMark[] }>(`/api/me/availability?from=${range.from}&to=${range.to}`),
  });
  const calendar = useQuery({
    queryKey: myEntriesKey(range),
    placeholderData: keepPreviousData,
    queryFn: () => api<{ person: { id: string; name: string } | null; assignments: MyAssignment[] }>(`/api/me/calendar?from=${range.from}&to=${range.to}`),
  });
  const questions = useMyQuestions();
  const tasks = useQuery({
    queryKey: [...PRIVATE_KEY, 'range', range.from, range.to],
    placeholderData: keepPreviousData,
    queryFn: () => api<{ tasks: PrivateTask[] }>(`${PRIVATE_BASE}?from=${range.from}&to=${range.to}`),
  });
  return { marks, calendar, questions, tasks };
}

export function useMyQuestions() {
  return useQuery({ queryKey: MY_QUESTIONS_KEY, queryFn: () => api<{ person: { id: string } | null; questions: MyQuestion[]; awaiting?: Awaiting[] }>('/api/me/questions') });
}

/**
 * Saving my days and my answers. Every save refreshes everything that shows the crew (`refreshCrew`: the grid, Obsada,
 * the side panel, the counters, this page). A „Niedostępny” on days I work comes back as a question (`ask`): `answerAsk`
 * resends it with the choice (`true` — „Tak”, I withdraw; `false` — „Nie”, those days stay as they were).
 */
export function useMySaves(onDone?: () => void) {
  const queryClient = useQueryClient();
  const [ask, setAsk] = useState<WithdrawQuestion | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const mark = useMutation({
    mutationFn: (body: MarkBody) => api<{ changed: number; withdrawn: Array<{ eventName: string; day: string }>; skipped: string[] }>('/api/me/availability', { method: 'POST', body }),
    onSuccess: (result) => {
      setAsk(null);
      setDone(result.withdrawn.length > 0 ? `Wycofano z obsady: ${[...new Set(result.withdrawn.map((w) => w.eventName))].join(', ')}.` : null);
      onDone?.();
    },
    onError: (error, body) => {
      if (error instanceof ApiError && error.code === 'withdraw_question') {
        const { withdraw: _w, assignmentIds: _a, ...asked } = body;
        setAsk({ body: asked, message: error.message, assignmentIds: ((error.details ?? {}) as { assignmentIds?: string[] }).assignmentIds ?? [] });
      }
    },
    onSettled: () => refreshCrew(queryClient),
  });
  // owner 2026-10-30 21:31: an answer changes only the newest point of the question's history — the one I saw
  // (`basedOn`); something newer meanwhile is a 409 `history_moved`, said, and the list is read again (`refreshCrew`)
  const basedOn = (id: string) => queryClient.getQueryData<{ questions: MyQuestion[] }>(MY_QUESTIONS_KEY)?.questions.find((q) => q.id === id)?.latestPointId;
  const answer = useMutation({
    mutationFn: ({ id, days, basedOn: seen }: { id: string; days: Record<string, boolean>; basedOn?: string | undefined }) => api<MyQuestion>(`/api/me/questions/${id}/answer`, { method: 'POST', body: { days, ...(seen ? { basedOn: seen } : {}) } }),
    onSettled: () => refreshCrew(queryClient),
  });
  const markError = mark.error && !(mark.error instanceof ApiError && mark.error.code === 'withdraw_question') ? errorText(mark.error) : null;
  return {
    mark: (body: MarkBody) => mark.mutate(body),
    marking: mark.isPending,
    markError,
    ask,
    answerAsk: (withdraw: boolean) => ask && mark.mutate({ ...ask.body, withdraw, assignmentIds: ask.assignmentIds }),
    closeAsk: () => setAsk(null),
    done,
    clearDone: () => setDone(null),
    answer: (id: string, day: string, yes: boolean) => answer.mutate({ id, days: { [day]: yes }, basedOn: basedOn(id) }),
    answering: answer.isPending ? answer.variables : null,
    answerError: answer.error ? errorText(answer.error) : null,
  };
}

export const errorText = (e: unknown) => (e instanceof ApiError ? e.message : 'Nie udało się zapisać — spróbuj ponownie.');

/**
 * „Mój kalendarz (N)” in the menu: how many of my questions wait for an answer and can still get one. Under the crew's
 * key, so every save (`refreshCrew`) recounts; read again every few minutes and when the window comes back.
 */
export function useMyQuestionCount(enabled: boolean): number {
  const q = useQuery({ queryKey: [...MY_QUESTIONS_KEY, 'count'], enabled, staleTime: 60_000, refetchInterval: 5 * 60_000, retry: false, queryFn: () => api<{ count: number }>('/api/me/questions/count') });
  return q.data?.count ?? 0;
}
